import { useRouter } from '@uirouter/react';
import { RefreshCw, Trash2, User as UserIcon } from 'lucide-react';

import { notifySuccess } from '@/portainer/services/notifications';
import { useUserMembership } from '@/portainer/users/queries';
import { isInitialAdmin } from '@/portainer/users/user.helpers';
import { Role, RoleNames, User, UserId } from '@/portainer/users/types';
import { useAccessTokens } from '@/react/portainer/account/access-tokens/queries/useAccessTokens';
import { useAllScopedKubeconfigs } from '@/react/portainer/scoped-kubeconfigs/queries/useAllScopedKubeconfigs';

import { ResourceDetailHeader } from '@@/ResourceDetailHeader/ResourceDetailHeader';
import { HeaderStats } from '@@/ResourceDetailHeader/HeaderStats';
import { ResourceStatBlock } from '@@/ResourceDetailHeader/ResourceStatBlock';
import { ActionBarShell } from '@@/ResourceDetailHeader/ActionBarShell';
import { ActionBarButton } from '@@/ResourceDetailHeader/ActionBarButton';
import { Badge } from '@@/Badge';
import { confirmDelete } from '@@/modals/confirm';

import { useDeleteUserMutation } from '../queries/useDeleteUserMutation';

export function UserHeader({
  userId,
  user,
  isLoading,
  viewerIsAdmin,
  isSelf,
  canSeeTokens,
  onRefresh,
}: {
  userId: UserId;
  user?: User;
  isLoading: boolean;
  viewerIsAdmin: boolean;
  isSelf: boolean;
  canSeeTokens: boolean;
  onRefresh(): void;
}) {
  const router = useRouter();
  const deleteMutation = useDeleteUserMutation();

  const membershipsQuery = useUserMembership(userId, { enabled: !!user });
  const tokensQuery = useAccessTokens(userId, {
    enabled: !!user && canSeeTokens,
  });
  const kubeconfigsQuery = useAllScopedKubeconfigs({
    enabled: !!user,
    select: (scopedKubeconfigs) =>
      scopedKubeconfigs.filter(
        (scopedKubeconfig) => scopedKubeconfig.userId === userId
      ),
  });

  const canDelete = viewerIsAdmin && !isSelf && !isInitialAdmin(userId);

  const actionBar = user ? (
    <ActionBarShell>
      <div className="flex items-center gap-3">
        <ActionBarButton
          icon={RefreshCw}
          onClick={onRefresh}
          data-cy="user-header-refresh"
        >
          Refresh
        </ActionBarButton>
      </div>
      {canDelete && (
        <div className="flex items-center gap-1">
          <ActionBarButton
            icon={Trash2}
            onClick={handleDelete}
            data-cy="user-header-delete"
            color="dangerlight"
          >
            Delete
          </ActionBarButton>
        </div>
      )}
    </ActionBarShell>
  ) : undefined;

  return (
    <ResourceDetailHeader
      icon={
        <UserIcon className="h-6 w-6 text-group-accent-8 th-highcontrast:text-white th-dark:text-group-accent-2" />
      }
      subtitleLabel="User"
      title={user?.Username ?? ''}
      isLoading={isLoading}
      badge={
        user && (
          <Badge type={user.Role === Role.Standard ? 'muted' : 'info'}>
            {RoleNames[user.Role]}
          </Badge>
        )
      }
      rightInfo={
        <HeaderStats>
          <CountBlock label="Teams" count={membershipsQuery.data?.length} />
          {canSeeTokens && (
            <CountBlock label="Tokens" count={tokensQuery.data?.length} />
          )}
          <CountBlock
            label="Kubeconfigs"
            count={kubeconfigsQuery.data?.length}
          />
        </HeaderStats>
      }
      actionBar={actionBar}
    />
  );

  async function handleDelete() {
    const confirmed = await confirmDelete(
      `Do you want to remove the user "${user?.Username}"? They will not be able to log in to Portainer anymore.`
    );

    if (!confirmed) {
      return;
    }

    deleteMutation.mutate(userId, {
      onSuccess() {
        notifySuccess('Success', 'User removed');
        router.stateService.go('portainer.users');
      },
    });
  }
}

function CountBlock({ label, count }: { label: string; count?: number }) {
  return (
    <ResourceStatBlock data-cy={`user-stat-${label.toLowerCase()}`}>
      <ResourceStatBlock.Label>{label}</ResourceStatBlock.Label>
      <ResourceStatBlock.Value size="lg">
        {count ?? '-'}
      </ResourceStatBlock.Value>
    </ResourceStatBlock>
  );
}
